import React from 'react';
import { 
  Users, ShieldCheck, Zap, Globe, Award, Clock, 
  BarChart3, BadgeCheck, Rocket, Briefcase, 
  Cpu, MessageSquare, Microscope, ChevronRight, 
  TrendingUp, Search
} from 'lucide-react';

const ENGINE_DATA = {
  totalTalent: "4,280",
  verifiedRate: "94%",
  seniority: [
    { label: 'Architect / Principal', value: 32, color: 'bg-indigo-500' },
    { label: 'Staff Engineer', value: 38, color: 'bg-blue-500' },
    { label: 'Senior / Lead', value: 30, color: 'bg-emerald-500' },
  ],
  scaleMetrics: [
    { label: 'Internet Scale (10M+)', count: '850+', icon: <Globe size={18}/> },
    { label: 'Microservices Mastery', count: '2.4k', icon: <Cpu size={18}/> },
    { label: 'Event-Driven Systems', count: '1.2k', icon: <Zap size={18}/> },
  ],
  reputation: [
    { label: 'Open Source Contributors', count: '120+', icon: <Cpu size={16}/> },
    { label: 'Patent Holders', count: '45', icon: <Microscope size={16}/> },
    { label: 'Conf. Speakers', count: '85', icon: <MessageSquare size={16}/> },
  ],
  impact: [
    "Avg. $450k infrastructure savings per hire",
    "92% success rate in distributed migrations",
    "65% hold final hiring authority in current roles"
  ]
};

const TalentIntelligenceEngine = () => {
  return (
    <div className="bg-[#f8fafc] min-h-screen p-4 md:p-10 font-sans text-slate-900">
      <div className="max-w-7xl mx-auto">
        
        {/* --- TOP ARCHITECTURE: HEADER & QUICK STATS --- */}
        <header className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-10 gap-8">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest">
              <BadgeCheck size={14} className="animate-pulse" /> Engineering Intelligence V3
            </div>
            <h1 className="text-5xl font-black tracking-tight text-slate-900">
              The Talent <span className="text-indigo-600">Density</span> Matrix
            </h1>
            <p className="text-slate-500 text-xl max-w-2xl font-medium">
              We don't source candidates; we index impact. Access a pre-vetted network of 
              <span className="text-slate-800 font-bold"> high-scale engineering leaders</span> ready for day-one contribution.
            </p>
          </div>

          <div className="flex gap-4 w-full lg:w-auto">
            <div className="flex-1 lg:flex-none bg-white p-6 rounded-3xl shadow-sm border border-slate-200">
              <div className="text-slate-400 text-xs font-bold uppercase mb-1">Total Network</div>
              <div className="text-3xl font-black text-indigo-600 flex items-center gap-2">
                <Users /> {ENGINE_DATA.totalTalent}
              </div>
            </div>
            <div className="flex-1 lg:flex-none bg-slate-900 p-6 rounded-3xl shadow-xl text-white">
              <div className="text-slate-400 text-xs font-bold uppercase mb-1">Match Accuracy</div>
              <div className="text-3xl font-black text-emerald-400">{ENGINE_DATA.verifiedRate}</div>
            </div>
          </div>
        </header>

        {/* --- BENTO GRID LAYOUT --- */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          
          {/* 1. LEADERSHIP COMPOSITION (5 Cols) */}
          <div className="md:col-span-5 bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-8 text-slate-100 group-hover:text-indigo-50 transition-colors">
              <TrendingUp size={120} strokeWidth={3} />
            </div>
            <div className="relative z-10">
              <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-8 flex items-center gap-2">
                <ShieldCheck size={16} className="text-indigo-500"/> Seniority Composition
              </h3>
              <div className="space-y-8">
                {ENGINE_DATA.seniority.map((s, i) => (
                  <div key={i} className="group/item">
                    <div className="flex justify-between items-end mb-3">
                      <div>
                        <div className="text-lg font-bold text-slate-800 leading-none">{s.label}</div>
                        <div className="text-xs text-slate-400 mt-1 font-medium">Verified Authority</div>
                      </div>
                      <div className="text-2xl font-black text-slate-900">{s.value}%</div>
                    </div>
                    <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${s.color} rounded-full transition-all duration-1000 ease-out`} 
                        style={{ width: `${s.value}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10 p-4 bg-indigo-50 rounded-2xl border border-indigo-100 text-indigo-800 text-sm font-semibold flex items-center gap-3">
                <Search size={18} /> Deep-search by Specific Cloud Architecture
              </div>
            </div>
          </div>

          {/* 2. ENGINEERING SCALE (7 Cols) */}
          <div className="md:col-span-7 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {ENGINE_DATA.scaleMetrics.map((m, i) => (
                <div key={i} className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm hover:border-indigo-300 transition-colors cursor-default">
                  <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 mb-4">
                    {m.icon}
                  </div>
                  <div className="text-2xl font-black text-slate-900">{m.count}</div>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-tight">{m.label}</div>
                </div>
              ))}
            </div>

            {/* IMPACT LEDGER CARD */}
            <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden">
              <div className="absolute -right-10 -bottom-10 w-64 h-64 bg-indigo-600/20 rounded-full blur-3xl"></div>
              <h3 className="text-xs font-black uppercase tracking-widest text-indigo-300 mb-6 flex items-center gap-2">
                <Award size={16} /> Business Impact Ledger
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <ul className="space-y-4">
                  {ENGINE_DATA.impact.map((text, i) => (
                    <li key={i} className="flex gap-3 text-sm font-medium text-slate-300">
                      <div className="mt-1 bg-emerald-500/20 p-1 rounded">
                        <Zap size={12} className="text-emerald-400" />
                      </div>
                      {text}
                    </li>
                  ))}
                </ul>
                <div className="bg-white/5 backdrop-blur-md rounded-3xl p-6 border border-white/10">
                   <div className="text-xs font-bold text-indigo-300 uppercase mb-4">Market Readiness</div>
                   <div className="flex justify-between items-center mb-4">
                      <span className="text-sm">Avg. Notice Period</span>
                      <span className="font-bold text-emerald-400">18 Days</span>
                   </div>
                   <div className="flex justify-between items-center">
                      <span className="text-sm">Active Explorers</span>
                      <span className="font-bold text-emerald-400">85%</span>
                   </div>
                   <button className="w-full mt-6 py-3 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-xs font-black uppercase transition-all flex items-center justify-center gap-2">
                      View Live Pipeline <ChevronRight size={14} />
                   </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- REPUTATION FOOTER --- */}
        <footer className="mt-8 flex flex-col md:flex-row items-center justify-between gap-6 bg-white border border-slate-200 p-6 rounded-[2rem]">
          <div className="flex flex-wrap gap-8 items-center justify-center md:justify-start">
            {ENGINE_DATA.reputation.map((r, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="text-indigo-500">{r.icon}</div>
                <div>
                  <div className="text-sm font-black text-slate-900 leading-none">{r.count}</div>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{r.label}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 w-full md:w-auto">
             <div className="hidden lg:block text-right">
                <div className="text-sm font-bold text-slate-900">Elite Network Access</div>
                <div className="text-xs text-slate-400">Limited to 5 seats per organization</div>
             </div>
             <button className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-4 rounded-2xl font-black text-sm transition-all transform hover:scale-[1.02] shadow-xl shadow-indigo-200">
               REQUEST ACCESS
             </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default TalentIntelligenceEngine;